import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTABand } from "@/components/CTABand";
import { Seo, localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema } from "@/seo/Seo";
import { SERVICES, getService, BUSINESS } from "@/data/servicesData";
import { AREAS } from "@/data/areasData";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!service) return <Navigate to="/services" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.short, path: `/services/${service.slug}` },
  ];
  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div className="bg-white min-h-screen" data-testid="service-page">
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        path={`/services/${service.slug}`}
        image={service.image}
        keywords={service.keywords}
        jsonLd={[localBusinessSchema(), serviceSchema(service), faqSchema(service.faqs), breadcrumbSchema(crumbs)]}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-[110px] sm:pt-[130px] pb-14 blueprint">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Breadcrumbs items={crumbs} />
            <span className="inline-block mt-5 bg-[#EAB308] border-2 border-[#0A1A4A] px-3 py-1 font-mono-accent text-[10px] tracking-widest uppercase">Swansea & Gower</span>
            <h1 className="font-display font-black text-[#0A1A4A] text-4xl sm:text-6xl tracking-tight leading-[0.95] mt-4">
              {service.name}<br /><span className="text-[#2563EB]">in Swansea</span>
            </h1>
            <p className="text-lg text-[#0A1A4A]/70 mt-6 leading-relaxed">{service.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {service.benefits.map((b) => (
                <span key={b} className="flex items-center gap-2 border-2 border-[#0A1A4A] px-3 py-2 text-sm font-medium">
                  <Check size={15} className="text-[#2563EB]" /> {b}
                </span>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="border-2 border-[#0A1A4A] shadow-hard overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
            <img src={service.image} alt={`${service.name} in Swansea`} className="w-full h-[360px] lg:h-[460px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Body + jobs */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="font-mono-accent text-xs tracking-widest uppercase text-[#2563EB] mb-3">/ About This Service</div>
          <p className="text-[#0A1A4A]/80 text-lg leading-relaxed">{service.body}</p>

          <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mt-12 mb-6">What We Cover</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {service.jobs.map((j) => (
              <div key={j} className="flex items-center gap-3 border-l-2 border-[#2563EB] pl-4 py-1">
                <Check size={18} className="text-[#2563EB] shrink-0" /> <span className="text-[#0A1A4A]">{j}</span>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mt-14 mb-6">Frequently Asked Questions</h2>
          <div className="border-2 border-[#0A1A4A]">
            {service.faqs.map((f, i) => (
              <details key={i} className="group border-b-2 border-[#0A1A4A] last:border-b-0" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none px-6 py-5 font-display font-extrabold text-[#0A1A4A] flex justify-between items-center hover:bg-[#F3F4F6]">
                  {f.q}<span className="text-[#2563EB] text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-6 pb-5 text-[#0A1A4A]/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="bg-[#0A1A4A] text-white p-7 border-2 border-[#0A1A4A]">
            <div className="font-mono-accent text-[10px] tracking-widest uppercase text-[#EAB308]">{service.contactForPricing ? service.priceLabel : "Pricing"}</div>
            {service.contactForPricing ? (
              <>
                <div className="font-display font-black text-3xl mt-2">{service.priceValue}</div>
                <p className="text-white/60 text-sm mt-3">{service.priceSub}</p>
              </>
            ) : (
              <>
                <div className="font-display font-black text-4xl mt-2">£{BUSINESS.minCharge}<span className="text-lg font-body font-normal text-white/60"> min / job</span></div>
                <p className="text-white/60 text-sm mt-3">Final price quoted by email once we've reviewed your job. No surprises.</p>
              </>
            )}
          </div>
          <div className="border-2 border-[#0A1A4A] p-7">
            <div className="font-display font-extrabold uppercase text-[#0A1A4A] mb-4">Areas We Cover</div>
            <div className="flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <Link key={a.slug} to={`/areas/${a.slug}`} className="flex items-center gap-1.5 border-2 border-[#0A1A4A] px-3 py-1.5 text-sm hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-colors">
                  <MapPin size={13} /> {a.area}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </section>

      {/* Related */}
      <section className="bg-[#F3F4F6] border-y-2 border-[#0A1A4A] py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mb-8">More Services</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group bg-white border-2 border-[#0A1A4A] p-6 hover:bg-[#2563EB] transition-colors" data-testid={`related-${r.slug}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-display font-extrabold uppercase text-lg text-[#0A1A4A] group-hover:text-white transition-colors">{r.short}</h3>
                  <ArrowUpRight className="text-[#2563EB] group-hover:text-white transition-colors" />
                </div>
                <p className="text-sm text-[#0A1A4A]/70 group-hover:text-white/80 transition-colors mt-2">{r.intro.slice(0, 90)}…</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand heading="NEED THIS DONE IN SWANSEA?" sub={service.metaDescription} />
      <Footer />
    </div>
  );
}
